<script>
import useVuelidate from '@vuelidate/core';
import moment from 'moment/moment';
import {required} from '@vuelidate/validators';

/* COMPOSANTS */
import AkButtonValidation from '@components/v2/general/AkButtonValidation';
import AkFormSubmitted from '@components/general/AkFormSubmitted';
import AkSidebar from '@components/v2/general/AkSidebar';
import GaMessages from '@components/ga/layout/GaMessages';
import AkAutocomplete from '@components/v2/input/AkAutocomplete.vue';
import AkDropdown from '@components/v2/input/AkDropdown.vue';

/* MIXINS */
import utilsMixin from '@mixins/utilsMixin';
import messageMixin from '@mixins/messageMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import accommodationAutocompleteMixin from '@mixins/autocomplete/accommodationAutocompleteMixin';

/* SERVICES */
import comptaCrgService from '@services/comptaCrgService';
import accommodationService from '@services/accommodationService';

export default {
  components: {
    AkDropdown,
    AkButtonValidation,
    AkFormSubmitted,
    AkAutocomplete,
    AkSidebar,
    GaMessages,
  },
  mixins: [messageMixin, utilsMixin, permissionsMixin, accommodationAutocompleteMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  emits: ['done'],
  data() {
    return {
      rapport: {},
      submitted: false,
      visible: false,
      accommodations: [],
      loading: false,
    };
  },
  validations() {
    return {
      rapport: {
        accommodationId: {required},
        year: {required},
      },
    };
  },
  mounted() {
    if (this.isOwner()) this.autocompleteAccommodation();
  },
  methods: {
    show() {
      this.rapport = {};
      this.submitted = false;
      this.visible = true;

      if (this.isOwner()) {
        this.loading = true;
        accommodationService.resume({enabled: true}).then((data) => {
          this.accommodations = data;
          if (this.accommodations.length === 1) this.rapport.accommodationId = this.accommodations[0].id;
          this.loading = false;
        });
      }
    },
    hide() {
      this.submitted = false;
      this.visible = false;
    },
    handle() {
      this.submitted = true;

      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.submit.init();
        return;
      }
      comptaCrgService
        .exportTds(this.rapport)
        .then(() => {
          this.$refs.submit.init();
          this.hide();
          this.success('Rapport TDS téléchargé');
        })
        .catch((e) => {
          this.$refs.submit.init();
          this.hide();
          if (e.response) {
            let message = this.msgForErr(this.$t, e);
            this.addError(message);
          } else {
            this.addError('Une erreur est survenue, merci de réessayer plus tard');
          }
        });
    },
  },
  computed: {
    years() {
      var today = moment();
      var until = 2023; //TODO: this year will be dynamic for each accommodation when the final tds screen will be specified
      var yearNow = today.year();
      var dp = [];
      for (let i = until; i <= yearNow; i++) {
        dp.push({display: i, id: i});
      }
      return dp.reverse();
    },
  },
};
</script>

<template>
  <AkSidebar :baseZIndex="9998" v-model:visible="visible" block-scroll position="right" :dismissable="true">
    <template #container>
      <div class="mb-4">
        <GaMessages ref="sidebarMessages" />
      </div>

      <div class="p-sidebar-header">
        <div class="p-sidebar-header-content">Rapport TDS</div>
        <button class="p-sidebar-close p-sidebar-icon p-link" type="button" @click="hide">
          <span
            class="p-sidebar-close-icon ga-icon ga-close_circle text-4xl text-grey-400 md:hover:text-grey-900 md:hover:bg-transparent" />
        </button>
      </div>

      <div class="p-sidebar-content">
        <perfect-scrollbar class="h-full flex flex-col gap-4 pr-7">
          <AkFormSubmitted :submitted="submitted" class="flex flex-col gap-4" reference="form" @submit="handle()">
            <div class="flex flex-col gap-2">
              <AkDropdown
                v-if="isOwner()"
                v-model="rapport.accommodationId"
                option-label="name"
                :loading="this.loading"
                :validator="v$.rapport.accommodationId"
                option-value="id"
                :label="$t('accommodation.short')"
                :options="this.accommodations" />

              <AkAutocomplete
                v-else
                ref="accommodationAutocomplete"
                v-model="rapport.accommodationId"
                option-value="id"
                option-label="display"
                :minLengthToLaunchSearch="3"
                @item-select="itemSelect"
                :placeholder="$t('accommodation.short')"
                :label="$t('accommodation.short')"
                :search-method="this.autocompleteAccommodation"
                :search-label="this.autocompleteAccommodationQuery"
                :init-method="this.autocompleteAccommodationById" />

              <AkDropdown
                v-model="rapport.year"
                option-label="display"
                option-value="id"
                :validator="v$.rapport.year"
                label="Année"
                :options="this.years" />
              <div class="flex justify-end">
                <AkButtonValidation ref="submit" :label="$t('download')" @action="handle" />
              </div>
            </div>
          </AkFormSubmitted>
        </perfect-scrollbar>
      </div>
    </template>
  </AkSidebar>
</template>
